/**
 * Shift start/stop control plus the consent gate.
 *
 * Consent is requested before the first shift rather than at the first ping:
 * being asked to approve location tracking in the middle of a timed prompt is
 * both a worse experience and a worse consent (it is effectively coerced by
 * the countdown).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Coffee, Loader2, MapPin, Play, Square, Users } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { captureLocation } from '../lib/geolocation';
import { useAuth } from '../store/auth';
import type {
  BreaksState,
  BreakType,
  Consent,
  MeetingPeriod,
  MeetingType,
  MyShift,
  ShiftSession,
} from '../types';

const NOTICE_VERSION = '1.0';

/**
 * The server's label for a break type.
 *
 * Read from the allowance payload rather than held in a map here: the labels,
 * the budgets and the windows are one table on the server, and a second copy
 * in the client is a thing to forget when a fourth break is added.
 */
function breakLabel(breaks: BreaksState | undefined, type: MeetingType): string {
  return (
    breaks?.allowances.find((a) => a.breakType === type)?.label ?? 'break'
  );
}

export default function ShiftBar() {
  const queryClient = useQueryClient();
  const [pickingMeeting, setPickingMeeting] = useState(false);
  const [pickingBreak, setPickingBreak] = useState(false);
  const [note, setNote] = useState('');
  // An account with no company has no shift policy, no office and no geofence,
  // so nothing will ever schedule a ping for them. Showing the banner asked an
  // owner to consent to a capture that cannot happen, and the click crashed on
  // a NOT NULL company_id. The API refuses it too; this stops it being offered.
  const hasCompany = useAuth((state) => state.user?.companyId) != null;

  const { data: session, isLoading } = useQuery<ShiftSession | null>({
    queryKey: ['current-shift'],
    queryFn: async () => (await client.get('/api/v1/attendance/shift/current')).data,
  });

  /**
   * The assigned window, which /shift/current cannot carry: it returns null
   * exactly when the start button is on screen.
   */
  const { data: myShift } = useQuery<MyShift>({
    queryKey: ['my-shift'],
    queryFn: async () => (await client.get('/api/v1/attendance/my-shift')).data,
  });

  /**
   * Polled rather than read once: the server ends a meeting on its own after
   * three hours, so a tab left open must not keep offering to end one that is
   * already over.
   */
  const { data: meeting } = useQuery<MeetingPeriod | null>({
    queryKey: ['current-meeting'],
    queryFn: async () => (await client.get('/api/v1/attendance/meeting/current')).data,
    refetchInterval: 60_000,
  });

  const startMeeting = useMutation({
    mutationFn: async (meetingType: MeetingType) =>
      (
        await client.post('/api/v1/attendance/meeting/start', {
          meetingType,
          note: note.trim() || null,
        })
      ).data,
    onSuccess: () => {
      setPickingMeeting(false);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['current-meeting'] });
      // The prompt should stop appearing immediately, not at the next poll.
      void queryClient.invalidateQueries({ queryKey: ['pending-ping'] });
      toast.success('Check-ins paused');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const endMeeting = useMutation({
    mutationFn: async () =>
      (await client.post('/api/v1/attendance/meeting/end')).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['current-meeting'] });
      void queryClient.invalidateQueries({ queryKey: ['pending-ping'] });
      void queryClient.invalidateQueries({ queryKey: ['todays-pings'] });
      toast.success('Check-ins resumed');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  /**
   * Polled on the same cadence as the meeting, and for the same reason: a
   * break ends on its own at its cap, so a tab left open must not keep
   * offering to end one the server closed twenty minutes ago. It also carries
   * what is left of each allowance, which falls as the day goes on.
   */
  const { data: breaks } = useQuery<BreaksState>({
    queryKey: ['breaks'],
    queryFn: async () => (await client.get('/api/v1/attendance/breaks')).data,
    refetchInterval: 60_000,
  });

  const activeBreak =
    breaks?.active && breaks.allowances.some(
      (a) => a.breakType === breaks.active?.meetingType,
    )
      ? breaks.active
      : null;

  const startBreak = useMutation({
    mutationFn: async (breakType: BreakType) =>
      (
        await client.post('/api/v1/attendance/break/start', {
          breakType,
          note: note.trim() || null,
        })
      ).data,
    onSuccess: () => {
      setPickingBreak(false);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['breaks'] });
      void queryClient.invalidateQueries({ queryKey: ['current-meeting'] });
      // The prompt should stop appearing immediately, not at the next poll.
      void queryClient.invalidateQueries({ queryKey: ['pending-ping'] });
      toast.success('Check-ins paused');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const endBreak = useMutation({
    mutationFn: async () =>
      (await client.post('/api/v1/attendance/break/end')).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['breaks'] });
      void queryClient.invalidateQueries({ queryKey: ['pending-ping'] });
      void queryClient.invalidateQueries({ queryKey: ['todays-pings'] });
      toast.success('Check-ins resumed');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const { data: consent } = useQuery<Consent | null>({
    queryKey: ['consent'],
    queryFn: async () => (await client.get('/api/v1/attendance/consent')).data,
  });

  const grantConsent = useMutation({
    mutationFn: async () =>
      (
        await client.post('/api/v1/attendance/consent', {
          noticeVersion: NOTICE_VERSION,
          granted: true,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consent'] });
      toast.success('Location sharing enabled');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const startShift = useMutation({
    mutationFn: async () => {
      const geo = await captureLocation();
      return (
        await client.post('/api/v1/attendance/shift/start', {
          location: geo.ok
            ? {
                latitude: geo.location.latitude,
                longitude: geo.location.longitude,
                accuracyM: geo.location.accuracyM,
              }
            : null,
        })
      ).data as ShiftSession;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['current-shift'] });
      queryClient.invalidateQueries({ queryKey: ['pending-ping'] });
      toast.success('Shift started', {
        description: `${data.scheduledPingCount} check-ins scheduled for today.`,
      });
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not start your shift')),
  });

  const endShift = useMutation({
    mutationFn: async () => (await client.post('/api/v1/attendance/shift/end')).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-shift'] });
      queryClient.invalidateQueries({ queryKey: ['pending-ping'] });
      queryClient.invalidateQueries({ queryKey: ['my-attendance'] });
      toast.success('Shift ended');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (isLoading) return null;
  // Nothing here applies without a company. Silent rather than an
  // explanation: an owner is not missing out on a feature, the feature is
  // simply about employees.
  if (!hasCompany) return null;

  if (!consent?.isActive) {
    return (
      <div className="rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200">
        <div className="flex items-start gap-3">
          <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-amber-900">
              Location Sharing Needed
            </h3>
            <p className="mt-1 text-sm text-amber-800">
              During your shift you will be asked to check in roughly every 10
              minutes. Your location is recorded with each check-in, only while a
              shift is running, and you can view or withdraw it at any time.
            </p>
            <button
              type="button"
              className="btn-primary mt-3"
              onClick={() => grantConsent.mutate()}
              disabled={grantConsent.isPending}
            >
              {grantConsent.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              I agree
            </button>
          </div>
        </div>
      </div>
    );
  }

  const open = session?.isOpen ?? false;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white p-4 ring-1 ring-gray-200">
      <div className="flex items-center gap-3">
        <span
          className={`h-2.5 w-2.5 rounded-full ${
            open ? 'animate-pulse bg-emerald-500' : 'bg-gray-300'
          }`}
        />
        <div>
          <p className="text-sm font-medium text-gray-900">
            {open ? 'Shift running' : 'Shift not started'}
          </p>
          {/* The start time and nothing else.
              What used to sit here — the check-in count, office-versus-field,
              a sound toggle, a notifications offer — was four pieces of
              standing configuration reported once a day at the one moment
              somebody is trying to start working. None of it changes during
              the shift and none of it is a decision the bar is asking for. */}
          {open && session && (
            <p className="text-xs text-gray-500">
              Started {new Date(session.startedAt).toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          )}

          {/* Kept, deliberately, and it is not the same kind of text as the
              rest. This one is a fact recorded against the person reading it,
              on the day it happened, and it names the assigned start the
              line above cannot. Shown to them rather than only to a manager. */}
          {open && session && (session.lateByMinutes ?? 0) > 0 && (
            <p className="mt-1 text-xs text-amber-700">
              Shift started late (assigned start{' '}
              {session.scheduledStartAt
                ? new Date(session.scheduledStartAt).toLocaleTimeString('en-IN', {
                    hour: 'numeric',
                    minute: '2-digit',
                  })
                : '—'}
              , started{' '}
              {new Date(session.startedAt).toLocaleTimeString('en-IN', {
                hour: 'numeric',
                minute: '2-digit',
              })}
              )
            </p>
          )}
        </div>
      </div>

      {open ? (
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* The end control lives here rather than on the check-in prompt,
                because while a meeting is running there is no prompt — that is
                the entire point of it. */}
            {meeting ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => endMeeting.mutate()}
                disabled={endMeeting.isPending}
              >
                {endMeeting.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Users className="h-4 w-4" />
                )}
                End meeting
              </button>
            ) : (
              !activeBreak && (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setPickingBreak(false);
                    setPickingMeeting((v) => !v);
                  }}
                >
                  <Users className="h-4 w-4" />
                  Meeting mode
                </button>
              )
            )}

            {activeBreak ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => endBreak.mutate()}
                disabled={endBreak.isPending}
              >
                {endBreak.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Coffee className="h-4 w-4" />
                )}
                End {breakLabel(breaks, activeBreak.meetingType).toLowerCase()}
              </button>
            ) : (
              !meeting && (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setPickingMeeting(false);
                    setPickingBreak((v) => !v);
                  }}
                >
                  <Coffee className="h-4 w-4" />
                  Take a break
                </button>
              )
            )}

            <button
              type="button"
              className="btn-secondary"
              onClick={() => endShift.mutate()}
              disabled={endShift.isPending}
            >
              {endShift.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              End shift
            </button>
          </div>

          {/* What it does to their day, in the place where they decide to do
              it. Paused check-ins are excluded rather than forgiven, and
              somebody should not have to be told that by their manager. */}
          {(meeting ?? activeBreak) && (
            <p className="text-xs text-indigo-700">
              Check-ins paused since{' '}
              {new Date((meeting ?? activeBreak)!.startedAt).toLocaleTimeString(
                'en-IN',
                { hour: 'numeric', minute: '2-digit' },
              )}
              {(meeting ?? activeBreak)!.note
                ? ` · ${(meeting ?? activeBreak)!.note}`
                : ''}{' '}
              · resumes on its own at{' '}
              {new Date((meeting ?? activeBreak)!.cutoffAt).toLocaleTimeString(
                'en-IN',
                { hour: 'numeric', minute: '2-digit' },
              )}
            </p>
          )}

          {pickingMeeting && !meeting && (
            <div className="w-72 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-left">
              <input
                spellCheck={true}
                className="input text-sm"
                placeholder="Note (optional) — e.g. Acme review"
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-primary px-2.5 py-1 text-xs"
                  onClick={() => startMeeting.mutate('client')}
                  disabled={startMeeting.isPending}
                >
                  Client meeting
                </button>
                <button
                  type="button"
                  className="btn-secondary px-2.5 py-1 text-xs"
                  onClick={() => startMeeting.mutate('team')}
                  disabled={startMeeting.isPending}
                >
                  Team meeting
                </button>
                <button
                  type="button"
                  className="btn-ghost px-2.5 py-1 text-xs"
                  onClick={() => setPickingMeeting(false)}
                >
                  Cancel
                </button>
              </div>
              <p className="text-[11px] text-gray-500">
                Check-ins pause until you end it, your shift ends, or three
                hours pass. Paused check-ins are not counted for or against you.
              </p>
            </div>
          )}

          {pickingBreak && !activeBreak && !meeting && (
            <div className="w-72 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-left">
              <div className="space-y-1.5">
                {breaks?.allowances.map((allowance) => (
                  <button
                    key={allowance.breakType}
                    type="button"
                    className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-left text-xs enabled:hover:border-gray-300 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                    onClick={() => startBreak.mutate(allowance.breakType)}
                    disabled={!allowance.availableNow || startBreak.isPending}
                    /* The server's refusal, verbatim. Rewriting it here would
                       be a second implementation of the window arithmetic. */
                    title={allowance.unavailableReason ?? undefined}
                  >
                    <span className="font-medium">{allowance.label}</span>
                    <span className="tabular-nums">
                      {allowance.remainingMinutes} min left
                    </span>
                  </button>
                ))}
              </div>

              {/* Named rather than left to a tooltip nobody opens: the one
                  thing worth knowing before tapping is which of these is shut
                  right now, and why. */}
              {breaks?.allowances.some((a) => !a.availableNow) && (
                <p className="text-[11px] text-gray-500">
                  {
                    breaks.allowances.find((a) => !a.availableNow)
                      ?.unavailableReason
                  }
                </p>
              )}

              <button
                type="button"
                className="btn-ghost px-2.5 py-1 text-xs"
                onClick={() => setPickingBreak(false)}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-right">
        <button
          type="button"
          className="btn-primary"
          onClick={() => startShift.mutate()}
          disabled={startShift.isPending || myShift?.canStartNow === false}
          title={
            myShift?.canStartNow === false
              ? `Your shift starts at ${myShift.startsAtLabel}`
              : undefined
          }
        >
          {startShift.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          Start shift
        </button>
        {/* Said out loud, not just as a tooltip on a dead button. A control
            that does nothing without explaining why reads as broken. The
            server refuses this regardless — see the 409 on /shift/start;
            this is so nobody has to discover the rule by hitting it. */}
        {myShift?.canStartNow === false && myShift.startsAtLabel && (
          <p className="mt-1.5 text-xs text-amber-700">
            Your shift starts at {myShift.startsAtLabel} — you can&rsquo;t
            check in yet
          </p>
        )}
        </div>
      )}
    </div>
  );
}
